// Where the practice screen is (video + start time): restored from the URL on
// boot, mirrored back to the URL and localStorage, and moved to the account's
// resume point (STORY-005). No Vue; the screen passes a `host` adapter.

export default class PositionSync {
  /**
   * @param {Object} host store, getRoute, getRouter, getDrill, getLang
   */
  constructor (host) {
    this.host = host;
    // loadSavedSettings() restores the last video and fires the videoId
    // watcher before applyUrl() applies the link's ?v=. True until then, so that
    // self-inflicted change isn't mistaken for navigating away from a challenge.
    this.bootstrapping = true;
    // True when the URL named a position different from the stored one
    // (challenge link or Stats jump); it then outranks the account's resume point.
    this.urlOwnsPosition = false;
  }

  _player () {
    return this.host.store.state.player;
  }

  // URL wins over localStorage. Called right after loadSavedSettings with the
  // query captured at mount; the caller clears `bootstrapping` in the same tick.
  applyUrl (initialQuery) {
    const store = this.host.store;
    let urlParamsApplied = false;
    if (initialQuery.v && initialQuery.v !== this._player().videoId) {
      store.dispatch('player/setVideoId', initialQuery.v);
      urlParamsApplied = true;
    }
    if (initialQuery.t !== undefined) {
      // parseFloat: starts are fractional; truncating can land on the
      // previous sentence (Stats "practice this sentence" jump).
      const t = parseFloat(initialQuery.t);
      if (!isNaN(t) && t !== this._player().startTime) {
        store.dispatch('player/setStartTime', t);
        urlParamsApplied = true;
      }
    }
    this.urlOwnsPosition = urlParamsApplied;
    if (urlParamsApplied) {
      store.dispatch('settings/saveSettings');
    }
  }

  // Persist position: URL for in-tab refresh, localStorage for new sessions
  // (STORY-005). Not during bootstrap, or the restore would bump savedAt and
  // look newer than the account's resume point.
  onPositionChange () {
    this.syncUrlParams();
    if (!this.bootstrapping) this.host.store.dispatch('settings/saveSettings');
  }

  // Same {v, t} params the Stats "practice this sentence" jump uses.
  syncUrlParams () {
    const route = this.host.getRoute();
    const router = this.host.getRouter();
    if (!route || !router) return;
    var q = route.query;
    var v = this._player().videoId || '';
    var t = String(this._player().startTime || 0);
    if (q.v === v && q.t === t) return;
    router.replace({ query: { v: v, t: t } }).catch(function() {});
  }

  /**
   * Jump to where the account last practised, only before the user has started.
   */
  applyResumePoint (point) {
    if (!point || !point.videoId) return;
    // 1. Somebody named a position deliberately (challenge link, Stats jump).
    if (this.urlOwnsPosition) return;
    // 2. Never yank someone out of a live attempt.
    const status = this.host.getDrill().status;
    if (status !== 'loading' && status !== 'ready') return;
    // 3. A segment start only means something in one language's split.
    if (point.lang && point.lang !== this.host.getLang()) return;
    // 4. Must be newer than local, except on a full pull (first sign-in or
    //    account switch), where the local point predates the account.
    if (!point.fullPull) {
      let savedAt = 0;
      try {
        savedAt = JSON.parse(localStorage.getItem('shadowingAppState') || '{}').savedAt || 0;
      } catch (e) { /* unreadable -> treat as no local position */ }
      if (Date.parse(point.at) <= savedAt) return;
    }

    const { videoId, startTime } = this._player();
    if (point.videoId === videoId && Math.abs(point.startTime - startTime) <= 0.05) return;

    // Order matters: setVideoId clears `sentences` via its watcher, so the
    // startTime watcher can't fire STARTTIME_CHANGED against the old list.
    if (point.videoId !== videoId) {
      this.host.store.dispatch('player/setVideoId', point.videoId);
    }
    this.host.store.dispatch('player/setStartTime', point.startTime);
  }
}
